import React, { useState } from "react";
import CrossAttachmentAssessment from "./CrossAttachmentAssessment";
import axios from "./../../axios";
import { toast } from "react-toastify";
import assets from "../../assets/assets";
import ComponentType from "../../Enum/ComponentTypeEnum";
import { Plus, Sparkles, Trash2, Info, CheckCircle2 } from "lucide-react";
import {
  Box,
  Typography,
  Switch,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Tooltip,
  InputAdornment,
} from "@mui/material";

const CreateUploadType = ({
  question,
  nextQuestion,
  setQuestions,
  componentType,
  hasDescriptiveAutoAssessment,
  selectedBox,
}) => {
  let teacher_id = JSON.parse(localStorage.getItem("Teacher_id"));
  const [isValidFileType, setIsValidFileType] = useState(true);
  const [loader, setLoader] = useState(false);

  // 🔥 Question Bank check
  const isQuestionBank = componentType === ComponentType.QUESTION_BANK;

  // ---------- Subjective Auto-Evaluation helpers ----------
  const updateSubjective = (patch) => {
    setQuestions((prev) => {
      const isDisabling = patch.enabled === false;

      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...(prev[nextQuestion].subjective_evaluation || {
            enabled: true,
            expectedAnswer: "",
            rubric: [],
          }),
          ...patch,
          // 🔥 Sirf Question Bank ke liye enabled hamesha false
          ...(isQuestionBank ? { enabled: false } : {}),
          ...(isDisabling && !isQuestionBank ? { rubric: [] } : {}),
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleExpectedAnswerChange = (value) => {
    setQuestions((prev) => {
      const currentSubjective = prev[nextQuestion].subjective_evaluation || {
        enabled: isQuestionBank ? false : false,
        expectedAnswer: "",
        rubric: [],
      };

      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...currentSubjective,
          expectedAnswer: value,
          // 🔥 Question Bank ke liye enabled false, baaki ke liye current rehne do
          ...(isQuestionBank ? { enabled: false } : {}),
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleRubricChange = (index, field, value) => {
    // 🔥 Assessment/Homework mein guard, Question Bank mein nahi
    if (!question?.subjective_evaluation?.enabled && !isQuestionBank) return;

    setQuestions((prev) => {
      const current = prev[nextQuestion].subjective_evaluation || {
        enabled: true,
        expectedAnswer: "",
        rubric: [],
      };
      const updatedRubric = current.rubric.map((r, i) =>
        i === index
          ? {
              ...r,
              [field]: field === "maxMarks" ? Number(value) || 0 : value,
            }
          : r,
      );
      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...current,
          // 🔥 Question Bank ke liye false, Assessment ke liye true
          enabled: isQuestionBank ? false : true,
          rubric: updatedRubric,
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleAddRubric = () => {
    if (!question?.subjective_evaluation?.enabled && !isQuestionBank) return;

    setQuestions((prev) => {
      const current = prev[nextQuestion].subjective_evaluation || {
        enabled: false,
        expectedAnswer: "",
        rubric: [],
      };
      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...current,
          // 🔥 Question Bank ke liye false, Assessment ke liye true
          enabled: isQuestionBank ? false : true,
          rubric: [...current.rubric, { criterion: "", maxMarks: 0 }],
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const handleRemoveRubric = (index) => {
    if (!question?.subjective_evaluation?.enabled && !isQuestionBank) return;

    setQuestions((prev) => {
      const current = prev[nextQuestion].subjective_evaluation || {
        enabled: true,
        expectedAnswer: "",
        rubric: [],
      };

      const newRubric = current.rubric.filter((_, i) => i !== index);

      // 🔥 Agar saare rubric delete ho gaye, toh OFF kar do (sirf Non-QB ke liye)
      const shouldBeEnabled = isQuestionBank ? false : newRubric.length > 0;

      const updatedQuestion = {
        ...prev[nextQuestion],
        is_question_edited: true,
        subjective_evaluation: {
          ...current,
          enabled: shouldBeEnabled,
          rubric: newRubric,
        },
      };
      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  // ---------- Existing attachment handlers (unchanged) ----------
  const handleReasonChange = (index, value) => {
    setQuestions((prev) => {
      const updatedCorrectAnswer = prev[nextQuestion].correct_answers.map(
        (answer, i) =>
          i === index
            ? {
                ...answer,
                reason_for_answer: {
                  ...answer.reason_for_answer,
                  explanation: value,
                },
              }
            : answer,
      );

      const updatedQuestion = {
        ...prev[nextQuestion],
        correct_answers: updatedCorrectAnswer,
        is_question_edited: true,
      };

      const newQuestions = [...prev];
      newQuestions[nextQuestion] = updatedQuestion;
      return newQuestions;
    });
  };

  const validateFileType = (file) => {
    const isAssessmentOrQB =
      componentType === ComponentType.ASSESSMENT ||
      componentType === ComponentType.QUESTION_BANK;

    const allowedExtensions = isAssessmentOrQB
      ? /(\.jpg|\.jpeg|\.png|\.pdf)$/i
      : /(\.jpg|\.jpeg|\.png|\.pdf|\.doc|\.docx)$/i;

    if (
      !allowedExtensions.test(file.name) ||
      question?.attachments?.length > 5
    ) {
      setIsValidFileType(false);
      return false;
    }

    setIsValidFileType(true);
    return true;
  };

  const handleAttachmentChange = async (index, file) => {
    if (file) {
      const isUnder10MB = file.size < 10 * 1024 * 1024;
      if (!isUnder10MB) {
        toast.error("File must be under 10MB.");
        return;
      }

      try {
        setLoader(true);
        if (file.size < 10 * 1024 * 1024 && validateFileType(file)) {
          let formData = new FormData();
          formData.append("name", file.name);
          formData.append("uploadQuestionAttachment", file);
          let res = await axios.post(
            `/api/v1/question/uploadAttachment/teacherId/${teacher_id}`,
            formData,
          );
          res && setLoader(false);
          res &&
            setQuestions((prev) => {
              const updatedCorrectAnswer = prev[
                nextQuestion
              ].correct_answers.map((answer, i) =>
                i === index
                  ? {
                      ...answer,
                      reason_for_answer: {
                        ...answer.reason_for_answer,
                        attachment: res.data.data,
                      },
                    }
                  : answer,
              );

              const updatedQuestion = {
                ...prev[nextQuestion],
                correct_answers: updatedCorrectAnswer,
                is_question_edited: true,
              };

              const newQuestions = [...prev];
              newQuestions[nextQuestion] = updatedQuestion;
              return newQuestions;
            });
        } else {
          setIsValidFileType(false);
        }
      } catch (error) {
        setLoader(false);
        console.log(error);
      }
    }
  };

  // ---------- Decide which UI to render ----------
  const isAutoEvaluateMode =
    selectedBox === "auto-evaluate" ||
    selectedBox?.autoEvaluate === true ||
    selectedBox?.isAutoEvaluate === true ||
    selectedBox?.isAutoEval === true;

  const showSubjectiveAutoEval =
    hasDescriptiveAutoAssessment === true && isAutoEvaluateMode;

  // 🔥 UI Enabled state
  const enabled = isQuestionBank
    ? false // Question Library mein hamesha OFF (backend ke liye)
    : (question?.subjective_evaluation?.enabled ?? true);

  const rubric = question?.subjective_evaluation?.rubric || [];
  const totalRubricMarks = rubric.reduce(
    (sum, r) => sum + (Number(r.maxMarks) || 0),
    0,
  );

  // Theme tokens
  const ACCENT = "#f77f00";
  const ACCENT_SOFT = "#fff4e5";
  const BORDER = "#e6e9ef";
  const TEXT_PRIMARY = "#1f2733";
  const TEXT_SECONDARY = "#5b6472";
  const BG_SOFT = "#f8f9fb";

  return (
    <>
      {showSubjectiveAutoEval ? (
        <Box className="col-12 ml-1" sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: "14px",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            {/* Title bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 1.75,
                background: `linear-gradient(90deg, ${ACCENT_SOFT} 0%, #ffffff 70%)`,
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    background: ACCENT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                  }}
                >
                  <Sparkles size={18} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "15px",
                      color: TEXT_PRIMARY,
                      lineHeight: 1.2,
                    }}
                  >
                    {isQuestionBank
                      ? "Subjective Question Type"
                      : "Subjective Auto-Evaluation Setup"}
                  </Typography>
                  {!isQuestionBank && (
                    <Typography
                      sx={{ fontSize: "12px", color: TEXT_SECONDARY }}
                    >
                      AI-Assisted scoring for descriptive answers
                    </Typography>
                  )}
                </Box>
              </Box>

              <Tooltip title="AI Assisted Evaluation">
                <Chip
                  icon={<Sparkles size={12} color={ACCENT} />}
                  label="AI"
                  size="small"
                  sx={{
                    background: ACCENT_SOFT,
                    color: ACCENT,
                    fontWeight: 700,
                    fontSize: "11px",
                    border: `1px solid ${ACCENT}`,
                    "& .MuiChip-icon": { ml: "6px" },
                  }}
                />
              </Tooltip>
            </Box>

            <Box sx={{ p: 2.5 }}>
              {/* 🔥 TOGGLE HIDE SIRF QUESTION BANK KE LIYE */}
              {!isQuestionBank && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    mb: 2.5,
                    border: `1px solid ${enabled ? ACCENT : BORDER}`,
                    background: enabled ? ACCENT_SOFT : BG_SOFT,
                    borderRadius: "10px",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
                  >
                    {enabled ? (
                      <CheckCircle2 size={18} color={ACCENT} />
                    ) : (
                      <Info size={18} color={TEXT_SECONDARY} />
                    )}
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "13.5px",
                          color: TEXT_PRIMARY,
                        }}
                      >
                        Enable Auto-Evaluation (AI-Assisted)
                      </Typography>
                      <Typography
                        sx={{ fontSize: "11.5px", color: TEXT_SECONDARY }}
                      >
                        {enabled
                          ? "AI will evaluate the student's answer against the rubric below."
                          : "Turn on to let AI score descriptive answers. Only Expected Answer will be saved."}
                      </Typography>
                    </Box>
                  </Box>

                  <Switch
                    checked={enabled}
                    onChange={() => updateSubjective({ enabled: !enabled })}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: ACCENT,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: ACCENT,
                        },
                    }}
                  />
                </Box>
              )}

              {/* 🔥 REORDER: RUBRIC FIRST */}
              <Box sx={{ mb: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: TEXT_PRIMARY,
                      }}
                    >
                      Marking Rubric / Criteria
                    </Typography>
                    {/* 🔥 Warning sirf Assessment/Homework ke liye */}
                    {!enabled && !isQuestionBank && (
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: "#d97706",
                          mt: 0.5,
                          fontWeight: 500,
                        }}
                      >
                        * Rubrics are disabled because Auto-Evaluation is OFF.
                        Only Expected Answer will be saved.
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${rubric.length} ${
                      rubric.length === 1 ? "criterion" : "criteria"
                    }`}
                    size="small"
                    sx={{
                      background: BG_SOFT,
                      color: TEXT_SECONDARY,
                      fontSize: "11px",
                      fontWeight: 500,
                      opacity: enabled || isQuestionBank ? 1 : 0.6,
                    }}
                  />
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    opacity: enabled || isQuestionBank ? 1 : 0.6,
                    pointerEvents: enabled || isQuestionBank ? "auto" : "none",
                  }}
                >
                  <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow sx={{ background: BG_SOFT }}>
                        <TableCell
                          sx={{
                            width: 48,
                            fontWeight: 700,
                            fontSize: "12px",
                            color: TEXT_SECONDARY,
                            borderBottom: `1px solid ${BORDER}`,
                          }}
                        ></TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            color: TEXT_SECONDARY,
                            borderBottom: `1px solid ${BORDER}`,
                          }}
                        >
                          CRITERION
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 170,
                            fontWeight: 700,
                            fontSize: "12px",
                            color: TEXT_SECONDARY,
                            borderBottom: `1px solid ${BORDER}`,
                          }}
                        >
                          MAX MARKS
                        </TableCell>
                        <TableCell
                          sx={{
                            width: 52,
                            borderBottom: `1px solid ${BORDER}`,
                          }}
                        ></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rubric.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              textAlign: "center",
                              py: 3,
                              color: TEXT_SECONDARY,
                              fontSize: "12.5px",
                              fontStyle: "italic",
                            }}
                          >
                            No rubric added yet. Click "Add Rubric Criterion".
                          </TableCell>
                        </TableRow>
                      )}

                      {rubric.map((r, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": { background: "#fbfcfe" },
                          }}
                        >
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: 600,
                              fontSize: "12.5px",
                              color: TEXT_SECONDARY,
                              borderBottom: `1px solid ${BORDER}`,
                            }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            sx={{ borderBottom: `1px solid ${BORDER}` }}
                          >
                            <TextField
                              fullWidth
                              variant="standard"
                              placeholder="Enter criterion (e.g. Mentions Sun, Birds & Flowers)"
                              value={r.criterion}
                              onChange={(e) =>
                                handleRubricChange(
                                  index,
                                  "criterion",
                                  e.target.value,
                                )
                              }
                              disabled={!enabled && !isQuestionBank}
                              InputProps={{ disableUnderline: true }}
                              sx={{
                                "& .MuiInputBase-input": {
                                  fontSize: "13px",
                                  py: 0.75,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{ borderBottom: `1px solid ${BORDER}` }}
                          >
                            <TextField
                              type="number"
                              variant="outlined"
                              size="small"
                              value={r.maxMarks}
                              onChange={(e) =>
                                handleRubricChange(
                                  index,
                                  "maxMarks",
                                  e.target.value,
                                )
                              }
                              disabled={!enabled && !isQuestionBank}
                              inputProps={{ min: 0 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography
                                      sx={{
                                        color: ACCENT,
                                        fontWeight: 700,
                                        fontSize: "12px",
                                      }}
                                    >
                                      [
                                    </Typography>
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Typography
                                      sx={{
                                        color: ACCENT,
                                        fontWeight: 700,
                                        fontSize: "11px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      Marks]
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  background: BG_SOFT,
                                  fontSize: "13px",
                                  "& fieldset": { borderColor: BORDER },
                                  "&:hover fieldset": { borderColor: ACCENT },
                                  "&.Mui-focused fieldset": {
                                    borderColor: ACCENT,
                                  },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              borderBottom: `1px solid ${BORDER}`,
                            }}
                          >
                            <Tooltip title="Remove criterion">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveRubric(index)}
                                disabled={!enabled && !isQuestionBank}
                                sx={{
                                  color: "#dc3545",
                                  "&:hover": {
                                    background: "rgba(220,53,69,0.08)",
                                  },
                                }}
                              >
                                <Trash2 size={15} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}

                      {rubric.length > 0 && (
                        <TableRow sx={{ background: ACCENT_SOFT }}>
                          <TableCell></TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "13px",
                              color: TEXT_PRIMARY,
                            }}
                          >
                            Total Rubric Marks
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "13.5px",
                              color: ACCENT,
                            }}
                          >
                            [ {totalRubricMarks} / {totalRubricMarks} ]
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>

                <Button
                  startIcon={<Plus size={16} />}
                  onClick={handleAddRubric}
                  disabled={!enabled && !isQuestionBank}
                  variant="outlined"
                  sx={{
                    mt: 1.5,
                    borderColor: ACCENT,
                    color: ACCENT,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    px: 1.75,
                    py: 0.6,
                    "&:hover": {
                      borderColor: ACCENT,
                      background: ACCENT_SOFT,
                    },
                    "&.Mui-disabled": {
                      borderColor: BORDER,
                      color: TEXT_SECONDARY,
                    },
                  }}
                >
                  Add Rubric Criterion
                </Button>
              </Box>

              {/* 🔥 REORDER: EXPECTED ANSWER SECOND */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "13px",
                      color: TEXT_PRIMARY,
                    }}
                  >
                    Expected Answer / Key Concepts
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11.5px", color: TEXT_SECONDARY }}
                  >
                    (AI based Evaluation is based on Expected Answer)
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="e.g. The child says 'Good Morning' to the sun, the birds, the trees, and the flowers."
                  value={question?.subjective_evaluation?.expectedAnswer || ""}
                  onChange={(e) => handleExpectedAnswerChange(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontSize: "13.5px",
                      background: "#fff",
                      "& fieldset": { borderColor: BORDER },
                      "&:hover fieldset": { borderColor: ACCENT },
                      "&.Mui-focused fieldset": { borderColor: ACCENT },
                    },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </Box>
      ) : (
        // ================== OLD UI (Fallback for Assessment/Homework if AI is OFF) ==================
        <div className="col-12 ml-1">
          <div className="hw_mandatory_checkbox ml-1 mt-3 poppins-semibold">
            Add correct answer
          </div>
          {question.correct_answers.length > 0 &&
            question.correct_answers.map((cAnswer, index) => (
              <div key={index}>
                <div
                  className="hw_mcq_option_box d-flex align-items-center"
                  style={{ width: "100%" }}
                >
                  <input
                    onChange={(e) => handleReasonChange(index, e.target.value)}
                    className="hw_add_reason"
                    placeholder=""
                    value={cAnswer.reason_for_answer.explanation}
                  />
                  <div>
                    <input
                      onChange={(e) => {
                        handleAttachmentChange(index, e.target.files[0]);
                      }}
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      id={`attachFileForReason${nextQuestion}`}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor={`attachFileForReason${nextQuestion}`}
                      style={{
                        border: "1px solid #8D4B28",
                        color: "#8D4B28",
                        backgroundColor: "white",
                      }}
                      className="rounded-3 p-1 d-flex align-items-center justify-content-center poppins-semibold mt-2"
                    >
                      <img
                        src={assets.UploadIcon}
                        height="100%"
                        width="25px"
                        alt="Attach Icon"
                        style={{
                          objectFit: "contain",
                          maxHeight: "100%",
                        }}
                      />
                    </label>
                  </div>
                </div>
                {cAnswer.reason_for_answer.attachment && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "98%",
                    }}
                    className="mt-2"
                  >
                    <CrossAttachmentAssessment
                      attachment={cAnswer.reason_for_answer.attachment}
                      isReasonForAnswer={true}
                      isEditable={true}
                      setQuestions={setQuestions}
                      question={question}
                      nextQuestion={nextQuestion}
                      componentType={componentType}
                    />
                  </div>
                )}
                {loader && (
                  <img
                    style={{ width: "32px", height: "32px" }}
                    src="/gif/Loader.gif"
                  />
                )}

                {!isValidFileType && (
                  <p id="errorMsg">
                    Please upload a file with a valid extension (pdf, jpg, jpeg,
                    png, pptx, ppt, mp3, mp4), Max Limit = 5.
                  </p>
                )}
              </div>
            ))}
        </div>
      )}
    </>
  );
};

export default CreateUploadType;
